import { useSeoMeta } from "@unhead/react";
import { ExternalLink, Bird, ShieldCheck, Zap } from "lucide-react";

import { OpReturnTool } from "@/components/OpReturnTool";

const Index = () => {
  useSeoMeta({
    title: "Sparrow OP_RETURN Tool – Add data the easy way",
    description:
      "Add an OP_RETURN output to your Sparrow Wallet transaction in a few clicks. Paste your PSBT, enter a message, and load the finished PSBT back into Sparrow. Runs entirely in your browser.",
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-muted/30">
      {/* Hero */}
      <header className="relative overflow-hidden border-b border-border/60">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, hsl(var(--primary)) 0, transparent 40%), radial-gradient(circle at 80% 0%, hsl(var(--primary)) 0, transparent 35%)",
          }}
          aria-hidden
        />
        <div className="relative mx-auto max-w-3xl px-5 py-14 sm:py-20 text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 ring-1 ring-primary/20">
            <Bird className="h-9 w-9 text-primary" />
          </div>
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
            OP_RETURN for Sparrow
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-lg text-muted-foreground leading-relaxed">
            Sparrow has no button for this — this tool does it for you. Paste a
            PSBT (Base64) or a raw transaction (hex), enter your message, and
            load the result back. Completely offline, right in your browser.
          </p>

          <div className="mt-7 flex flex-wrap items-center justify-center gap-3 text-sm">
            <Feature icon={<ShieldCheck className="h-4 w-4" />} label="100% local" />
            <Feature icon={<Zap className="h-4 w-4" />} label="Up to 500 bytes" />
            <Feature
              icon={<Bird className="h-4 w-4" />}
              label="For Sparrow Wallet"
            />
          </div>
        </div>
      </header>

      {/* Tool */}
      <main className="mx-auto max-w-3xl px-5 py-10 sm:py-14">
        <OpReturnTool />
      </main>

      {/* Footer */}
      <footer className="border-t border-border/60">
        <div className="mx-auto max-w-3xl px-5 py-8 text-center text-sm text-muted-foreground space-y-3">
          <div className="flex items-center justify-center gap-4">
            <a
              href="https://github.com/sparrowwallet/sparrow/issues/97"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors"
            >
              <ExternalLink className="h-4 w-4" />
              Sparrow Issue #97
            </a>
          </div>
          <p>Not an official Sparrow product. Use at your own risk.</p>
          <p>
            Vibed with{" "}
            <a
              href="https://shakespeare.diy"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-primary hover:underline"
            >
              Shakespeare
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

function Feature({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-card px-3 py-1.5 text-muted-foreground">
      <span className="text-primary">{icon}</span>
      {label}
    </span>
  );
}

export default Index;
